import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { FormattedMessage } from 'react-intl';
import Avatar from '@material-ui/core/Avatar';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import formatCurrency from '@afosto/utils/formatCurrency';
import formatDate from '@afosto/utils/date/formatDate';
import Icon from '@afosto/app-components-react/Icon';
import SkeletonBody from '@afosto/components-react/SkeletonBody';
import { subscription as subscriptionApi } from '@afosto/iam/api/Subscription';
import translations from './translations';

const UpcomingInvoiceRow = ({ app, divider }) => {
  const intl = useIntl();
  const [data, setData] = useState({});

  useEffect(() => {
    let isMounted = true;

    const fetchUpcomingInvoiceForApp = async key => {
      try {
        const response = await subscriptionApi.getUpcoming({ code: key });

        if (isMounted) {
          setData(response.data);
        }
      } catch (error) {
        if (isMounted) {
          setData({});
        }
      }
    };

    fetchUpcomingInvoiceForApp(app.key);

    return () => {
      isMounted = false;
    };
  }, [app.key]);

  if (!data) {
    return (
      <>
        <ListItem>
          <ListItemAvatar>
            <Avatar>
              <Icon icon={['fal', app.icon]} size="small" />
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={intl.formatMessage({
              id: `pages.contract.apps.${app.key}.title`,
              defaultMessage: app.name,
              description: `The name of the app for code ${app.key}`,
            })}
            secondary={<SkeletonBody lines={1} style={{ maxWidth: 200, marginTop: 8 }} />}
          />
          <ListItemSecondaryAction>
            <ListItemText primary={<SkeletonBody lines={1} style={{ width: 75 }} />} />
          </ListItemSecondaryAction>
        </ListItem>
        {divider && <Divider variant="inset" />}
      </>
    );
  }

  return (
    <>
      <ListItem>
        <ListItemAvatar>
          <Avatar>
            <Icon icon={['fal', app.icon]} size="small" />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={
            <>
              <FormattedMessage
                id={`pages.contract.apps.${app.key}.title`}
                defaultMessage={app.label}
                description={`The name of the app for code ${app.key}`}
              />
              {data.is_trial && (
                <Box ml={1} component="span">
                  <Chip
                    size="small"
                    variant="outlined"
                    color="primary"
                    label={intl.formatMessage(translations.trialDiscount)}
                  />
                </Box>
              )}
            </>
          }
          secondary={`${data.start_at ? formatDate(new Date(data.start_at)) : ''} - ${
            data.end_at ? formatDate(new Date(data.end_at)) : ''
          }`}
        />
        <ListItemSecondaryAction>
          {data.total !== undefined && (
            <ListItemText
              primary={formatCurrency(data.total / 100 || 0)}
              primaryTypographyProps={{
                style: { fontWeight: '700' },
                component: 'div',
                align: 'right',
              }}
              secondary={
                data.is_trial && (
                  <>
                    <Typography variant="body2" color="textSecondary">
                      <FormattedMessage {...translations.trialDiscount} />{' '}
                      {formatCurrency((data.total / 100) * -1 || 0)}
                    </Typography>
                  </>
                )
              }
              secondaryTypographyProps={{ align: 'right' }}
            />
          )}
        </ListItemSecondaryAction>
      </ListItem>
      {divider && <Divider variant="inset" />}
    </>
  );
};

UpcomingInvoiceRow.propTypes = {
  app: PropTypes.object.isRequired,
  divider: PropTypes.bool,
};

UpcomingInvoiceRow.defaultProps = {
  divider: false,
};

export default UpcomingInvoiceRow;
